/**
 * Recruiting API service
 */
import axiosInstance from '../../../shared/api/axiosInstance';
import type { TagSummary } from '../../../shared/components/tags';
import { CandidateSource } from '../../../generated/api/models/candidateSource';
import type { RecruitingDashboard } from '../../../generated/api/models/recruitingDashboard';
import type { RelationType } from '../../../generated/api/models/relationType';

const BASE_URL = '/api/v1/recruiting';

// Types - use these until generated types are available
export interface HiringPipeline {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  is_default: boolean;
  stages?: Stage[];
  created_at: string;
  updated_at: string;
}

export interface Stage {
  id: string;
  tenant_id: string;
  pipeline_id: string;
  name: string;
  position: number;
  stage_type: string;
  color: string;
  is_terminal: boolean;
  auto_reject_days?: number;
  default_interview_type?: string | null;
  scorecard_template_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScorecardCriterion {
  name: string;
  description?: string;
  weight?: number;
  rating_scale?: number;
}

export interface InterviewQuestion {
  question: string;
  category?: string;
  guidance?: string;
}

export interface CriteriaScore {
  criteria_name: string;
  score: number;
  notes?: string;
}

export interface ScorecardTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  stage_type?: string;
  criteria: ScorecardCriterion[];
  interview_questions: InterviewQuestion[];
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface Department {
  id: string;
  tenant_id: string;
  name: string;
  parent_id?: string;
  head_user_id?: string;
  created_at: string;
  updated_at: string;
}

// The candidate_source value set is the single source of truth in
// shared/schemas/recruiting/candidate.schema.json → generated CandidateSource
// (const object + type). Alias the type and derive the option list from it so the
// enum is never hand-maintained here or in the forms that render the <Select>.
export type CandidateSourceType = CandidateSource;
export const CANDIDATE_SOURCE_VALUES: CandidateSourceType[] = Object.values(CandidateSource);

export interface SourceStat {
  source: string;
  count: number;
}

export interface AgentCompany {
  id: string;
  name: string;
  website?: string;
  phone?: string;
  city?: string;
  country?: string;
  recruiter_count: number;
  job_count: number;
}

export interface JobPosting {
  id: string;
  tenant_id: string;
  company_id: string;
  title: string;
  slug?: string;
  internal_code?: string;
  department_id?: string;
  department?: Department;
  employment_type?: string;
  experience_level?: string;
  location_type?: string;
  // New location scope fields
  location_scope?: 'offices' | 'countries' | 'regions' | 'global' | null;
  location_office_ids?: string[];
  location_country_codes?: string[];
  location_regions?: string[];
  // Legacy fields (deprecated but kept for compatibility)
  location_city?: string;
  location_state?: string;
  location_country?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency: string;
  salary_period: string;
  show_salary: boolean;
  description: string;
  requirements?: string;
  benefits?: string;
  pipeline_id: string;
  pipeline?: HiringPipeline;
  status: string;
  published_at?: string;
  closes_at?: string;
  hiring_manager_id?: string;
  recruiter_id?: string;
  application_notification_user_ids?: string[];
  target_hire_count: number;
  hired_count: number;
  /** Cross-cutting tags framework. */
  tags?: TagSummary[];
  created_at: string;
  updated_at: string;
  created_by?: string;
  /**
   * Sharing: the calling user's best relation under a restricted visibility mode.
   * Null/absent in everyone mode (legacy full access) — drives the Share
   * affordances + SharedWithMeChip.
   */
  user_relation?: RelationType | null;
}

export interface Candidate {
  id: string;
  tenant_id: string;
  company_ids: string[];
  email?: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  phone?: string;
  current_title?: string;
  current_company?: string;
  years_experience?: number;
  expected_salary?: number;
  expected_salary_currency?: string;
  expected_salary_period?: string;
  linkedin_url?: string;
  twitter_url?: string;
  portfolio_url?: string;
  github_url?: string;
  photo_file_id?: string;
  city?: string;
  state?: string;
  country?: string;
  willing_to_relocate?: boolean;
  resume_file_id?: string;
  candidate_source?: CandidateSourceType;
  agency_organization_id?: string;
  referred_by_employee_id?: string;
  /** Cross-cutting tags framework. */
  tags?: TagSummary[];
  last_activity_at?: string;
  applied_jobs?: Array<{
    job_posting_id: string;
    job_title: string;
    status: string;
  }>;
  created_at: string;
  updated_at: string;
  /**
   * Sharing: the calling user's best relation under a restricted visibility mode.
   * Null/absent in everyone mode (legacy full access). For a candidate visible only
   * via the job cascade (linked to a job the caller can see) this is `viewer`.
   */
  user_relation?: RelationType | null;
}

export type CandidateNotificationStatus = 'not_notified' | 'email_sent' | 'informed_manually';

export interface RejectionReason {
  id: string;
  tenant_id: string;
  name: string;
  category: string;
  is_active: boolean;
  position: number;
  created_at: string;
}

export interface Application {
  id: string;
  tenant_id: string;
  company_ids: string[];
  candidate_id: string;
  candidate?: Candidate;
  job_posting_id: string;
  job_posting?: JobPosting;
  stage_id: string;
  stage?: Stage;
  stage_entered_at: string;
  status: string;
  rejection_reason_id?: string;
  rejection_notes?: string;
  rejected_at?: string;
  rejected_by?: string;
  candidate_notification_status?: CandidateNotificationStatus;
  offer_salary?: number;
  offer_currency?: string;
  offer_start_date?: string;
  offer_accepted_at?: string;
  owner_id?: string;
  overall_rating?: number;
  cover_letter?: string;
  application_answers?: Record<string, unknown>;
  applied_at: string;
  last_activity_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Interview {
  id: string;
  tenant_id: string;
  application_id: string;
  format: string;
  interview_type?: string | null;
  title?: string;
  scheduled_at?: string;
  duration_minutes: number;
  timezone?: string;
  office_id?: string | null;
  office_name?: string | null;
  room?: string | null;
  video_link?: string;
  status: string;
  scorecard_template_id?: string;
  scorecard_template?: ScorecardTemplate;
  instructions?: string;
  candidate_prep?: string;
  calendar_event_id?: string;
  candidate_invite_sent_at?: string | null;
  planned_questions?: PlannedQuestion[];
  participants?: InterviewParticipant[];
  feedback?: InterviewFeedback[];
  created_at: string;
  updated_at: string;
  created_by?: string;
  // Enriched fields (from list_all and detail endpoints)
  candidate_id?: string;
  candidate_name?: string;
  job_title?: string;
  job_posting_id?: string;
}

export interface InterviewParticipant {
  id: string;
  interview_id: string;
  user_id: string;
  role: string;
  response_status: string;
  created_at: string;
  // Enriched field (from list_all endpoint)
  user_name?: string;
}

export interface InterviewFeedback {
  id: string;
  tenant_id: string;
  interview_id: string;
  submitted_by: string;
  overall_rating?: number;
  recommendation?: string;
  criteria_scores?: CriteriaScore[];
  strengths?: string;
  concerns?: string;
  notes?: string;
  is_private: boolean;
  submitted_at: string;
  updated_at: string;
}


export interface Activity {
  id: string;
  tenant_id: string;
  activity_type: string;
  subject_type: string;
  subject_id: string;
  application_id?: string;
  candidate_id?: string;
  job_posting_id?: string;
  actor_type: string;
  actor_id?: string;
  summary?: string;
  activity_metadata?: Record<string, unknown>;
  occurred_at: string;
  created_at: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// AI Match Score
export interface MatchScore {
  overall_score: number;
  skills_match: number;
  experience_match: number;
  matching_skills: string[];
  missing_skills: string[];
  strengths: string[];
  gaps: string[];
  summary: string;
}

// Cascading interview question (used at all levels)
export interface PlannedQuestion {
  id: string;
  question: string;
  category: string;
  guidance: string;
  difficulty: string;
  source: 'template' | 'ai' | 'manual';
}

export interface JobStageQuestions {
  id?: string;
  tenant_id?: string;
  job_posting_id: string;
  stage_id: string;
  questions: PlannedQuestion[];
  scorecard_template_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

// AI Interview Questions
export interface GeneratedQuestion {
  question: string;
  category: string;
  guidance: string;
  difficulty: string;
}

// AI Candidate Assessment
export interface CandidateAssessment {
  executive_summary: string;
  overall_score: number;
  recommendation: string;
  strengths: string[];
  concerns: string[];
  risk_factors: string[];
  interviewer_consensus: string;
  key_themes: string[];
  confidence_level: string;
  suggested_next_steps: string[];
}

// AI Research
export interface CandidateResearch {
  professional_presence: {
    summary: string;
    platforms_found: string[];
    notable_contributions: string[];
  };
  github_activity: {
    summary: string;
    notable_repos: string[];
    languages: string[];
  };
  publications_and_talks: string[];
  overall_assessment: string;
  data_quality: string;
}

// Dashboard Stats. GET /recruiting/reports/dashboard is on the shared KPI substrate
// (docs/platform/dashboard-kpi-endpoints.md), so its payload type is generated from
// shared/schemas/recruiting/dashboard.schema.json — aliased here rather than mirrored
// by hand.
export type DashboardStats = RecruitingDashboard;

// Pipeline Funnel
export interface PipelineFunnelEntry {
  stage: string;
  position: number;
  color: string;
  count: number;
}

// Source Effectiveness
export interface SourceEffectiveness {
  source: string;
  total_applications: number;
  hired: number;
  hire_rate: number;
}

// Time to Hire
export interface TimeToHire {
  average_days_to_hire: number;
  recent_hires: { job_title: string; days_to_hire: number }[];
}

// Settings types
export interface RecruitingTenantSettings {
  id?: string;
  tenant_id?: string;
  job_description_template: string | null;
  enable_ai_features: boolean;
  settings: Record<string, unknown>;
  updated_by?: string;
}

// Settings API
export const settingsApi = {
  getTenantSettings: async (): Promise<RecruitingTenantSettings> => {
    const response = await axiosInstance.get(`${BASE_URL}/settings/tenant`);
    return response.data;
  },

  updateTenantSettings: async (data: Partial<RecruitingTenantSettings>): Promise<RecruitingTenantSettings> => {
    const response = await axiosInstance.put(`${BASE_URL}/settings/tenant`, data);
    return response.data;
  },
};

// AI API
export interface GenerateJobContentRequest {
  job_title: string;
  employment_type?: string;
  experience_level?: string;
  location_type?: string;
  location_city?: string;
  additional_context?: string;
  template?: string;
}

export interface EditJobContentRequest {
  existing_content: string;
  field_name: string;
  instruction: string;
  job_title: string;
}

// Candidate AI extraction response
export interface CandidateExtractedData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  current_title?: string;
  current_company?: string;
  years_experience?: number;
  city?: string;
  state?: string;
  country?: string;
  linkedin_url?: string;
  github_url?: string;
  portfolio_url?: string;
  resume_text?: string;
}

export const candidateAiApi = {
  extractFromResume: async (fileId: string): Promise<CandidateExtractedData> => {
    const response = await axiosInstance.post(`${BASE_URL}/candidates/ai/extract-from-resume`, { file_id: fileId });
    return response.data.data;
  },

  extractFromLinkedin: async (linkedinUrl: string): Promise<CandidateExtractedData> => {
    const response = await axiosInstance.post(`${BASE_URL}/candidates/ai/extract-from-linkedin`, { linkedin_url: linkedinUrl });
    return response.data.data;
  },
};

export const jobAiApi = {
  generateContent: async (data: GenerateJobContentRequest): Promise<{ description: string; requirements: string; benefits: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs/ai/generate-content`, data);
    return response.data.data;
  },

  editContent: async (data: EditJobContentRequest): Promise<{ content: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs/ai/edit-content`, data);
    return response.data.data;
  },

  exportJob: async (jobId: string, format: 'pdf' | 'docx'): Promise<Blob> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs/${jobId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },
};

// Hiring Pipeline API
export const hiringPipelineApi = {
  list: async (): Promise<HiringPipeline[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines`);
    return response.data.data;
  },

  get: async (id: string): Promise<HiringPipeline> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines/${id}`);
    return response.data.data;
  },

  create: async (data: { name: string; description?: string; is_default?: boolean }): Promise<HiringPipeline> => {
    const response = await axiosInstance.post(`${BASE_URL}/pipelines`, data);
    return response.data.data;
  },

  createDefault: async (): Promise<HiringPipeline> => {
    const response = await axiosInstance.post(`${BASE_URL}/pipelines/default`);
    return response.data.data;
  },

  update: async (id: string, data: { name?: string; description?: string | null; is_default?: boolean }): Promise<HiringPipeline> => {
    const response = await axiosInstance.put(`${BASE_URL}/pipelines/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/pipelines/${id}`);
  },

  createStage: async (pipelineId: string, data: {
    name: string;
    position: number;
    stage_type?: string;
    color?: string;
    is_terminal?: boolean;
    auto_reject_days?: number;
    default_interview_type?: string;
    scorecard_template_id?: string;
  }): Promise<Stage> => {
    const response = await axiosInstance.post(`${BASE_URL}/pipelines/${pipelineId}/stages`, data);
    return response.data.data;
  },

  updateStage: async (stageId: string, data: Partial<Stage>): Promise<Stage> => {
    const response = await axiosInstance.put(`${BASE_URL}/stages/${stageId}`, data);
    return response.data.data;
  },

  deleteStage: async (stageId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/stages/${stageId}`);
  },
};

// Department API
export const departmentApi = {
  list: async (): Promise<Department[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/departments`);
    return response.data.data;
  },

  create: async (data: { name: string; parent_id?: string; head_user_id?: string }): Promise<Department> => {
    const response = await axiosInstance.post(`${BASE_URL}/departments`, data);
    return response.data.data;
  },
};

// Job API
export const jobApi = {
  list: async (params?: {
    status?: string;
    department_id?: string;
    hiring_manager_id?: string;
    recruiter_id?: string;
    search?: string;
    tags?: string;
    tags_mode?: 'any' | 'all';
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<JobPosting>> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs`, { params });
    return response.data;
  },

  get: async (id: string, options?: { include_pipeline?: boolean; include_questions?: boolean }): Promise<JobPosting> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs/${id}`, { params: options });
    return response.data.data;
  },

  create: async (data: {
    title: string;
    description?: string;
    pipeline_id: string;
    department_id?: string;
    employment_type?: string;
    experience_level?: string;
    location_type?: string;
    location_scope?: 'offices' | 'countries' | 'regions' | 'global' | null;
    location_office_ids?: string[];
    location_country_codes?: string[];
    location_regions?: string[];
    location_city?: string;
    location_state?: string;
    location_country?: string;
    salary_min?: number;
    salary_max?: number;
    salary_currency?: string;
    salary_period?: string;
    show_salary?: boolean;
    requirements?: string;
    benefits?: string;
    hiring_manager_id?: string;
    recruiter_id?: string;
    closes_at?: string;
    target_hire_count?: number;
  }): Promise<JobPosting> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs`, data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<JobPosting>): Promise<JobPosting> => {
    const response = await axiosInstance.put(`${BASE_URL}/jobs/${id}`, data);
    return response.data.data;
  },

  publish: async (id: string): Promise<JobPosting> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs/${id}/publish`);
    return response.data.data;
  },

  close: async (id: string, filled?: boolean): Promise<JobPosting> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs/${id}/close`, { filled });
    return response.data.data;
  },

  reopen: async (id: string): Promise<JobPosting> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs/${id}/reopen`);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/jobs/${id}`);
  },
};

// Job Interviewers API
export interface JobInterviewer {
  id: string;
  job_posting_id: string;
  user_id: string;
  role: string;
  interview_stages: string[];
  created_at: string;
}

export const jobInterviewerApi = {
  list: async (jobId: string): Promise<JobInterviewer[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs/${jobId}/interviewers`);
    return response.data.data;
  },

  add: async (jobId: string, data: { user_id: string; role?: string; interview_stages?: string[] }): Promise<JobInterviewer> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs/${jobId}/interviewers`, data);
    return response.data.data;
  },

  update: async (jobId: string, userId: string, data: { interview_stages: string[] }): Promise<JobInterviewer> => {
    const response = await axiosInstance.put(`${BASE_URL}/jobs/${jobId}/interviewers/${userId}`, data);
    return response.data.data;
  },

  remove: async (jobId: string, userId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/jobs/${jobId}/interviewers/${userId}`);
  },
};

// Job Task Board API
export interface JobBoardInfo {
  board_id: string | null;
  workspace_id: string | null;
  has_board: boolean;
}

export interface JobBoardCreateResult {
  board_id: string;
  workspace_id: string | null;
  is_new: boolean;
}

export const jobBoardApi = {
  /**
   * Get the task board for a job (if it exists).
   */
  get: async (jobId: string): Promise<JobBoardInfo> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs/${jobId}/board`);
    return response.data.data;
  },

  /**
   * Create or get the task board for a job.
   * Creates a new board if one doesn't exist, returns existing board otherwise.
   */
  create: async (jobId: string): Promise<JobBoardCreateResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs/${jobId}/board`);
    return response.data.data;
  },
};

// Candidate API
export const candidateApi = {
  list: async (params?: {
    candidate_source?: string;
    agency_organization_id?: string;
    referred_by_employee_id?: string;
    tags?: string;
    tags_mode?: 'any' | 'all';
    search?: string;
    has_applications?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Candidate>> => {
    const response = await axiosInstance.get(`${BASE_URL}/candidates`, { params });
    return response.data;
  },

  get: async (id: string, options?: { include_applications?: boolean }): Promise<Candidate> => {
    const response = await axiosInstance.get(`${BASE_URL}/candidates/${id}`, { params: options });
    return response.data.data;
  },

  create: async (data: {
    email?: string;
    first_name: string;
    last_name: string;
    phone?: string;
    current_title?: string;
    current_company?: string;
    years_experience?: number;
    expected_salary?: number;
    expected_salary_currency?: string;
    expected_salary_period?: string;
    linkedin_url?: string;
    twitter_url?: string;
    portfolio_url?: string;
    github_url?: string;
    photo_file_id?: string;
    city?: string;
    state?: string;
    country?: string;
    willing_to_relocate?: boolean;
    candidate_source?: string;
    agency_organization_id?: string;
    referred_by_employee_id?: string;
    tag_ids?: string[];
    resume_file_id?: string;
  }): Promise<Candidate> => {
    const response = await axiosInstance.post(`${BASE_URL}/candidates`, data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<Candidate>): Promise<Candidate> => {
    const response = await axiosInstance.put(`${BASE_URL}/candidates/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/candidates/${id}`);
  },

  listActivities: async (candidateId: string): Promise<Activity[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/candidates/${candidateId}/activities`);
    return response.data.data;
  },
};

// Application API
export const applicationApi = {
  listByJob: async (jobId: string, params?: {
    stage_id?: string;
    status?: string;
    owner_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Application>> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs/${jobId}/applications`, { params });
    return response.data;
  },

  listByCandidate: async (candidateId: string): Promise<Application[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/candidates/${candidateId}/applications`);
    return response.data.data;
  },

  get: async (id: string): Promise<Application> => {
    const response = await axiosInstance.get(`${BASE_URL}/applications/${id}`);
    return response.data.data;
  },

  create: async (data: {
    candidate_id: string;
    job_posting_id: string;
    owner_id?: string;
    cover_letter?: string;
    application_answers?: Record<string, unknown>;
  }): Promise<Application> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications`, data);
    return response.data.data;
  },

  moveToStage: async (id: string, stageId: string): Promise<Application> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications/${id}/move`, { stage_id: stageId });
    return response.data.data;
  },

  reject: async (id: string, data?: {
    rejection_reason_id?: string;
    rejection_notes?: string;
    candidate_notification_status?: CandidateNotificationStatus;
  }): Promise<Application> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications/${id}/reject`, data);
    return response.data.data;
  },

  hire: async (id: string, data?: {
    offer_salary?: number;
    offer_currency?: string;
    offer_start_date?: string;
    candidate_notification_status?: CandidateNotificationStatus;
  }): Promise<Application> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications/${id}/hire`, data);
    return response.data.data;
  },

  withdraw: async (id: string): Promise<Application> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications/${id}/withdraw`);
    return response.data.data;
  },

  getTimeline: async (id: string): Promise<Activity[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/applications/${id}/timeline`);
    return response.data.data;
  },

};

// Rejection Reasons API
export const rejectionReasonApi = {
  list: async (): Promise<RejectionReason[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/rejection-reasons`);
    return response.data.data;
  },
};

// Interview API
export const interviewApi = {
  listByApplication: async (applicationId: string): Promise<Interview[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/applications/${applicationId}/interviews`);
    return response.data.data;
  },

  listUpcoming: async (params?: { user_id?: string; limit?: number }): Promise<Interview[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/interviews/upcoming`, { params });
    return response.data.data;
  },

  listAll: async (params?: {
    search?: string;
    status?: string;
    format?: string;
    interview_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Interview>> => {
    const response = await axiosInstance.get(`${BASE_URL}/interviews`, { params });
    return response.data;
  },

  get: async (id: string): Promise<Interview> => {
    const response = await axiosInstance.get(`${BASE_URL}/interviews/${id}`);
    return response.data.data;
  },

  create: async (applicationId: string, data: {
    format: string;
    interview_type?: string | null;
    title?: string;
    scheduled_at?: string;
    duration_minutes?: number;
    timezone?: string;
    office_id?: string | null;
    room?: string | null;
    video_link?: string;
    scorecard_template_id?: string;
    instructions?: string;
    candidate_prep?: string;
    participants?: { user_id: string; role?: string }[];
    send_candidate_invite?: boolean;
    send_self_invite?: boolean;
  }): Promise<Interview> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications/${applicationId}/interviews`, data);
    return response.data.data;
  },

  createForCandidate: async (candidateId: string, data: {
    format: string;
    interview_type?: string | null;
    title?: string;
    scheduled_at?: string;
    duration_minutes?: number;
    timezone?: string;
    office_id?: string | null;
    room?: string | null;
    video_link?: string;
    scorecard_template_id?: string;
    instructions?: string;
    candidate_prep?: string;
    participants?: { user_id: string; role?: string }[];
    send_candidate_invite?: boolean;
    send_self_invite?: boolean;
  }): Promise<Interview> => {
    const response = await axiosInstance.post(`${BASE_URL}/candidates/${candidateId}/interviews`, data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<Interview> & { notify_candidate?: boolean }): Promise<Interview> => {
    const response = await axiosInstance.put(`${BASE_URL}/interviews/${id}`, data);
    return response.data.data;
  },

  cancel: async (id: string): Promise<Interview> => {
    const response = await axiosInstance.post(`${BASE_URL}/interviews/${id}/cancel`);
    return response.data.data;
  },

  complete: async (id: string): Promise<Interview> => {
    const response = await axiosInstance.post(`${BASE_URL}/interviews/${id}/complete`);
    return response.data.data;
  },

  getFeedback: async (id: string): Promise<InterviewFeedback[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/interviews/${id}/feedback`);
    return response.data.data;
  },

  submitFeedback: async (id: string, data: {
    overall_rating?: number;
    recommendation?: string;
    criteria_scores?: CriteriaScore[];
    strengths?: string;
    concerns?: string;
    notes?: string;
    is_private?: boolean;
  }): Promise<InterviewFeedback> => {
    const response = await axiosInstance.post(`${BASE_URL}/interviews/${id}/feedback`, data);
    return response.data.data;
  },

  downloadICS: async (id: string): Promise<Blob> => {
    const response = await axiosInstance.get(`${BASE_URL}/interviews/${id}/ics`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getCalendarLinks: async (id: string): Promise<{ google: string; outlook: string; yahoo: string }> => {
    const response = await axiosInstance.get(`${BASE_URL}/interviews/${id}/calendar-links`);
    return response.data.data;
  },

  sendInvite: async (id: string): Promise<{ success: boolean }> => {
    const response = await axiosInstance.post(`${BASE_URL}/interviews/${id}/send-invite`);
    return response.data.data;
  },

  addParticipant: async (interviewId: string, userId: string, role: string = 'interviewer'): Promise<Interview> => {
    const response = await axiosInstance.post(`${BASE_URL}/interviews/${interviewId}/participants`, {
      user_id: userId,
      role,
    });
    return response.data.data;
  },

  removeParticipant: async (interviewId: string, userId: string): Promise<Interview> => {
    const response = await axiosInstance.delete(`${BASE_URL}/interviews/${interviewId}/participants/${userId}`);
    return response.data.data;
  },
};

// Channel API
// Source Stats API
export const sourceStatsApi = {
  getJobSourceStats: async (jobId: string): Promise<SourceStat[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs/${jobId}/source-stats`);
    return response.data.data;
  },
};

// Agent (recruiting agency company) API
export const agentApi = {
  list: async (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: AgentCompany[]; total: number }> => {
    const response = await axiosInstance.get(`${BASE_URL}/agents`, { params });
    return response.data;
  },
};

export interface JobAgencyCompany {
  id: string;
  job_posting_id: string;
  organization_id: string;
  has_portal_access: boolean;
  created_at: string;
}

export interface SetAgencyCompanyEntry {
  organization_id: string;
  has_portal_access: boolean;
}

export const jobAgencyApi = {
  getCompanies: async (jobId: string): Promise<JobAgencyCompany[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs/${jobId}/agency-companies`);
    return response.data.data;
  },
  setCompanies: async (jobId: string, companies: SetAgencyCompanyEntry[]): Promise<JobAgencyCompany[]> => {
    const response = await axiosInstance.put(`${BASE_URL}/jobs/${jobId}/agency-companies`, {
      companies,
    });
    return response.data.data;
  },
};

// Scorecard Template API
export const scorecardTemplateApi = {
  list: async (): Promise<ScorecardTemplate[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/scorecard-templates`);
    return response.data.data;
  },

  get: async (id: string): Promise<ScorecardTemplate> => {
    const response = await axiosInstance.get(`${BASE_URL}/scorecard-templates/${id}`);
    return response.data.data;
  },

  create: async (data: {
    name: string;
    description?: string;
    stage_type?: string;
    criteria?: ScorecardCriterion[];
    interview_questions?: InterviewQuestion[];
    is_default?: boolean;
  }): Promise<ScorecardTemplate> => {
    const response = await axiosInstance.post(`${BASE_URL}/scorecard-templates`, data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<ScorecardTemplate>): Promise<ScorecardTemplate> => {
    const response = await axiosInstance.put(`${BASE_URL}/scorecard-templates/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/scorecard-templates/${id}`);
  },
};

// AI APIs
export const aiApi = {
  scoreApplication: async (applicationId: string): Promise<MatchScore> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications/${applicationId}/ai/match-score`);
    return response.data.data;
  },

  scoreCandidateForJob: async (candidateId: string, jobPostingId: string): Promise<MatchScore> => {
    const response = await axiosInstance.post(`${BASE_URL}/candidates/${candidateId}/ai/match-score/${jobPostingId}`);
    return response.data.data;
  },

  generateQuestions: async (applicationId: string, format: string, interviewType?: string | null): Promise<{ questions: GeneratedQuestion[] }> => {
    const response = await axiosInstance.post(`${BASE_URL}/interviews/ai/generate-questions`, {
      application_id: applicationId,
      format,
      ...(interviewType ? { interview_type: interviewType } : {}),
    });
    return response.data.data;
  },

  generateAssessment: async (applicationId: string): Promise<CandidateAssessment> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications/${applicationId}/ai/assessment`);
    return response.data.data;
  },

  researchCandidate: async (candidateId: string): Promise<CandidateResearch> => {
    const response = await axiosInstance.post(`${BASE_URL}/candidates/${candidateId}/ai/research`);
    return response.data.data;
  },
};

// Reports API
export const reportsApi = {
  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/dashboard`);
    return response.data;
  },

  getPipelineFunnel: async (jobPostingId?: string): Promise<PipelineFunnelEntry[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/pipeline-funnel`, {
      params: jobPostingId ? { job_posting_id: jobPostingId } : undefined,
    });
    return response.data.data;
  },

  getSourceEffectiveness: async (): Promise<SourceEffectiveness[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/source-effectiveness`);
    return response.data.data;
  },

  getTimeToHire: async (limit?: number): Promise<TimeToHire> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/time-to-hire`, {
      params: limit ? { limit } : undefined,
    });
    return response.data.data;
  },
};

// Bulk Operations API
export const bulkApi = {
  moveApplications: async (applicationIds: string[], stageId: string): Promise<{ moved: string[]; count: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications/bulk/move`, {
      application_ids: applicationIds,
      stage_id: stageId,
    });
    return response.data.data;
  },

  rejectApplications: async (applicationIds: string[], rejectionReasonId?: string, rejectionNotes?: string): Promise<{ rejected: string[]; count: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/applications/bulk/reject`, {
      application_ids: applicationIds,
      rejection_reason_id: rejectionReasonId,
      rejection_notes: rejectionNotes,
    });
    return response.data.data;
  },
};

// Offer types
export interface Offer {
  id: string;
  tenant_id: string;
  application_id: string;
  salary?: number;
  salary_currency: string;
  salary_period?: string;
  equity_description?: string;
  benefits_summary?: string;
  bonus?: string;
  start_date?: string;
  expiry_date?: string;
  status: string;
  approved_by?: string;
  approved_at?: string;
  offer_letter_content?: string;
  offer_letter_template_id?: string;
  sent_at?: string;
  responded_at?: string;
  response_notes?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Enriched
  first_name?: string;
  last_name?: string;
  candidate_email?: string;
  job_title?: string;
  job_posting_id?: string;
}

export interface SchedulingToken {
  id: string;
  tenant_id: string;
  application_id: string;
  // Plaintext token — returned ONLY by createLink (to build the candidate link).
  // The token is hashed at rest (token_hash); getInfo never returns it.
  token?: string;
  format: string;
  interview_type?: string | null;
  duration_minutes: number;
  interviewer_user_ids: string[];
  available_slots: { start: string; end: string }[];
  status: string;
  expires_at: string;
  selected_slot_start?: string;
  selected_slot_end?: string;
  interview_id?: string;
  // Enriched
  first_name?: string;
  last_name?: string;
  job_title?: string;
}

export interface CalendarInterview {
  id: string;
  format: string;
  interview_type?: string | null;
  title?: string;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  office_id?: string | null;
  office_name?: string | null;
  room?: string | null;
  video_link?: string;
  candidate_name: string;
  job_title: string;
}

// Offer API
export const offerApi = {
  list: async (params?: { application_id?: string; status?: string; limit?: number; offset?: number }): Promise<PaginatedResponse<Offer>> => {
    const response = await axiosInstance.get(`${BASE_URL}/offers`, { params });
    return response.data;
  },

  get: async (id: string): Promise<Offer> => {
    const response = await axiosInstance.get(`${BASE_URL}/offers/${id}`);
    return response.data.data;
  },

  create: async (data: {
    application_id: string;
    salary?: number;
    salary_currency?: string;
    salary_period?: string;
    equity_description?: string;
    benefits_summary?: string;
    bonus?: string;
    start_date?: string;
    expiry_date?: string;
    offer_letter_content?: string;
    notes?: string;
  }): Promise<Offer> => {
    const response = await axiosInstance.post(`${BASE_URL}/offers`, data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<Offer>): Promise<Offer> => {
    const response = await axiosInstance.put(`${BASE_URL}/offers/${id}`, data);
    return response.data.data;
  },

  submitForApproval: async (id: string): Promise<Offer> => {
    const response = await axiosInstance.post(`${BASE_URL}/offers/${id}/submit-for-approval`);
    return response.data.data;
  },

  approve: async (id: string): Promise<Offer> => {
    const response = await axiosInstance.post(`${BASE_URL}/offers/${id}/approve`);
    return response.data.data;
  },

  send: async (id: string): Promise<Offer> => {
    const response = await axiosInstance.post(`${BASE_URL}/offers/${id}/send`);
    return response.data.data;
  },

  respond: async (id: string, response: 'accepted' | 'declined', notes?: string): Promise<Offer> => {
    const res = await axiosInstance.post(`${BASE_URL}/offers/${id}/respond`, null, { params: { response, notes } });
    return res.data.data;
  },

  withdraw: async (id: string): Promise<Offer> => {
    const response = await axiosInstance.post(`${BASE_URL}/offers/${id}/withdraw`);
    return response.data.data;
  },
};

// Scheduling API
export const schedulingApi = {
  createLink: async (data: {
    application_id: string;
    format: string;
    interview_type?: string | null;
    duration_minutes?: number;
    interviewer_user_ids: string[];
    available_slots: { start: string; end: string }[];
    office_id?: string | null;
    room?: string | null;
    video_link?: string;
    instructions?: string;
    expires_in_days?: number;
  }): Promise<SchedulingToken> => {
    const response = await axiosInstance.post(`${BASE_URL}/scheduling/create-link`, data);
    return response.data.data;
  },

  getInfo: async (token: string): Promise<SchedulingToken> => {
    const response = await axiosInstance.get(`${BASE_URL}/scheduling/${token}`);
    return response.data.data;
  },

  selectSlot: async (token: string, slot: { slot_start: string; slot_end: string }): Promise<{ interview_id: string; scheduled_at: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/scheduling/${token}/select-slot`, slot);
    return response.data.data;
  },
};

// Calendar API
export const calendarApi = {
  getInterviews: async (start: string, end: string): Promise<CalendarInterview[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/interviews/calendar`, { params: { start, end } });
    return response.data.data;
  },
};

// Job Stage Questions API (Cascading Interview Questions)
export const jobStageQuestionsApi = {
  getQuestions: async (jobId: string, stageId: string): Promise<{ questions: JobStageQuestions; inherited_questions: PlannedQuestion[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs/${jobId}/stages/${stageId}/questions`);
    return response.data.data;
  },

  getAllForJob: async (jobId: string): Promise<JobStageQuestions[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/jobs/${jobId}/stage-questions`);
    return response.data.data;
  },

  updateQuestions: async (jobId: string, stageId: string, questions: PlannedQuestion[], scorecardTemplateId?: string | null): Promise<JobStageQuestions> => {
    const payload: { questions: PlannedQuestion[]; scorecard_template_id?: string | null } = { questions };
    if (scorecardTemplateId !== undefined) {
      payload.scorecard_template_id = scorecardTemplateId;
    }
    const response = await axiosInstance.put(`${BASE_URL}/jobs/${jobId}/stages/${stageId}/questions`, payload);
    return response.data.data;
  },

  copyFromTemplate: async (jobId: string): Promise<JobStageQuestions[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs/${jobId}/copy-stage-questions`);
    return response.data.data;
  },

  aiRefine: async (jobId: string, stageId: string, existingQuestions: PlannedQuestion[]): Promise<{ questions: PlannedQuestion[] }> => {
    const response = await axiosInstance.post(`${BASE_URL}/jobs/${jobId}/stages/${stageId}/questions/ai-refine`, {
      existing_questions: existingQuestions,
    });
    return response.data.data;
  },
};

// Interview Planned Questions API
export const plannedQuestionsApi = {
  getQuestions: async (interviewId: string): Promise<{ planned_questions: PlannedQuestion[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/interviews/${interviewId}/planned-questions`);
    return response.data.data;
  },

  updateQuestions: async (interviewId: string, questions: PlannedQuestion[]): Promise<{ planned_questions: PlannedQuestion[] }> => {
    const response = await axiosInstance.put(`${BASE_URL}/interviews/${interviewId}/planned-questions`, { questions });
    return response.data.data;
  },

  aiRefine: async (interviewId: string, existingQuestions: PlannedQuestion[]): Promise<{ questions: PlannedQuestion[] }> => {
    const response = await axiosInstance.post(`${BASE_URL}/interviews/${interviewId}/planned-questions/ai-refine`, {
      existing_questions: existingQuestions,
    });
    return response.data.data;
  },
};
